// Centralized Stripe Checkout launcher.
//
// LOVABLE-ONLY CRITICAL ROUTING — DO NOT REMOVE THE COBRAIN BRANCH.
// solo/studio (HireEva consumer plans) are served by the canonical
// ElectricSheep Supabase project (rhfojelkgtwcxnrfhtlj). The `cobrain` plan
// edge function is ONLY deployed on the Lovable Cloud project
// (dvddfvigkzikieuqrtcn) for this app and MUST be routed there. Any sync
// from GitHub main that drops the cobrain branch below will return
// `Invalid plan.` (HTTP 400) in production and break CoBrain checkout.
const HIREEVA_CHECKOUT_ENDPOINT =
  "https://rhfojelkgtwcxnrfhtlj.supabase.co/functions/v1/create-stripe-checkout";
const COBRAIN_CHECKOUT_ENDPOINT = `${
  import.meta.env.VITE_SUPABASE_URL ?? "https://dvddfvigkzikieuqrtcn.supabase.co"
}/functions/v1/create-stripe-checkout`;
const COBRAIN_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;

export type CheckoutPlan = "solo" | "studio" | "cobrain";
export type CheckoutCycle = "monthly" | "quarterly" | "semiannual";

export const ELECTRIC_SHEEP_ONBOARDING_URL =
  "https://www.electricsheephq.com/founding-onboarding";
export const CHECKOUT_SESSION_PLACEHOLDER = "{CHECKOUT_SESSION_ID}";

export function resolveReturnUrl(origin: string, value: string): string {
  if (/^https?:\/\//i.test(value)) return value;
  const path = value.startsWith("/") ? value : `/${value}`;
  return `${origin}${path}`;
}

export function defaultSuccessUrl(plan: CheckoutPlan): string {
  switch (plan) {
    case "cobrain":
      return `/cobrain?checkout=success&session_id=${CHECKOUT_SESSION_PLACEHOLDER}`;
    case "studio":
      return `${ELECTRIC_SHEEP_ONBOARDING_URL}?session_id=${CHECKOUT_SESSION_PLACEHOLDER}&tier=biz`;
    case "solo":
    default:
      return `${ELECTRIC_SHEEP_ONBOARDING_URL}?session_id=${CHECKOUT_SESSION_PLACEHOLDER}&tier=solo`;
  }
}

const SEO_CTX_KEY = "hireeva:last_seo_checkout_context";

function readStoredSeoContext(): { source_slug?: string; source_keyword?: string; source?: string } {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(SEO_CTX_KEY);
    if (!raw) return {};
    const j = JSON.parse(raw) as Record<string, unknown>;
    const slug = (j.source_slug ?? j.sourceSlug ?? j.seo_slug) as string | undefined;
    const keyword = (j.source_keyword ?? j.sourceKeyword ?? j.seo_keyword) as string | undefined;
    const source = j.source as string | undefined;
    return { source_slug: slug, source_keyword: keyword, source };
  } catch { return {}; }
}

export async function startCheckout(opts: {
  plan: CheckoutPlan;
  cycle?: CheckoutCycle;
  seats?: number;
  successPath?: string;
  cancelPath?: string;
  source?: string;
  sourceSlug?: string;
  sourceKeyword?: string;
  source_slug?: string;
  source_keyword?: string;
  metadata?: Record<string, string | number | boolean | null | undefined>;
}): Promise<{ url?: string; error?: string }> {
  const ambassadorCode =
    typeof window !== "undefined" ? localStorage.getItem("eva_ref") : null;
  const origin = typeof window !== "undefined" ? window.location.origin : "https://hireeva.co";

  // Normalize SEO attribution: explicit args win, then metadata fields,
  // then localStorage fallback for users that arrived from an SEO page
  // earlier in their session.
  const md = (opts.metadata ?? {}) as Record<string, unknown>;
  const stored = readStoredSeoContext();
  const source_slug =
    opts.sourceSlug ?? opts.source_slug ??
    (md.source_slug as string | undefined) ?? (md.seo_slug as string | undefined) ??
    stored.source_slug;
  const source_keyword =
    opts.sourceKeyword ?? opts.source_keyword ??
    (md.source_keyword as string | undefined) ?? (md.seo_keyword as string | undefined) ??
    stored.source_keyword;
  const source =
    opts.source ?? (md.source as string | undefined) ??
    (source_slug ? "hireeva_seo_sprint" : stored.source);

  const payload = {
    plan: opts.plan,
    cycle: opts.cycle ?? "monthly",
    ...(opts.seats != null ? { seats: opts.seats } : {}),
    ambassador_code: ambassadorCode,
    ...(source ? { source } : {}),
    ...(source_slug ? { source_slug } : {}),
    ...(source_keyword ? { source_keyword } : {}),
    ...(opts.metadata ? { metadata: opts.metadata } : {}),
    success_url: resolveReturnUrl(origin, opts.successPath ?? defaultSuccessUrl(opts.plan)),
    cancel_url: resolveReturnUrl(origin, opts.cancelPath ?? "/#pricing"),
  };

  const isCobrain = opts.plan === "cobrain";
  const endpoint = isCobrain ? COBRAIN_CHECKOUT_ENDPOINT : HIREEVA_CHECKOUT_ENDPOINT;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (isCobrain && COBRAIN_ANON_KEY) {
    headers.apikey = COBRAIN_ANON_KEY;
    headers.Authorization = `Bearer ${COBRAIN_ANON_KEY}`;
  }

  try {
    const res = await fetch(endpoint, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });
    const data = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
    if (!res.ok) return { error: data?.error || `Checkout failed (${res.status})` };
    return data ?? {};
  } catch (e) {
    return { error: (e as Error).message || "Network error starting checkout." };
  }
}

/** Open a checkout session in a new tab. Resolves once the URL is requested. */
export async function openCheckout(opts: Parameters<typeof startCheckout>[0]): Promise<string | null> {
  const { url, error } = await startCheckout(opts);
  if (url) {
    window.open(url, "_blank", "noopener,noreferrer");
    return null;
  }
  return error || "Could not start checkout. Please try again.";
}
